import {
  Button,
  Descriptions,
  Drawer,
  List,
  Space,
  Spin,
  Tag,
  Typography,
  notification,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import UserAvatar from 'components/user-avatar';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { LeaveRequest, LeaveRequestStatus } from 'types/leave-request';
import {
  LEAVE_STATUS_COLOR,
  LEAVE_STATUS_LABEL,
  formatLeaveDays,
  getPersonName,
} from '../helpers/leave-request-format';
import { mapLeaveRequestError } from '../helpers/leave-request-error';
import { ReviewAction } from './review-leave-request-modal';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';

interface Props {
  requestId: number | null;
  refreshKey: number;
  canReview: boolean;
  onClose: () => void;
  onReview: (request: LeaveRequest, action: ReviewAction) => void;
}

function dateTime(value?: string | null) {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—';
}

const LeaveRequestDetailDrawer = ({
  requestId,
  refreshKey,
  canReview,
  onClose,
  onReview,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [request, setRequest] = useState<LeaveRequest | null>(null);

  useEffect(() => {
    if (!requestId) {
      setRequest(null);
      return;
    }
    let cancelled = false;
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(`${API.leaveRequests}/${requestId}`);
        if (!cancelled) setRequest(res?.data || null);
      } catch (err) {
        if (!cancelled) notification.error({ message: mapLeaveRequestError(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [requestId, refreshKey]);

  const canReviewRequest = canReview && request?.status === LeaveRequestStatus.PENDING;
  const user = request?.user;

  return (
    <Drawer
      title="Chi tiết phiếu nghỉ phép"
      open={!!requestId}
      onClose={onClose}
      width={620}
      destroyOnClose
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : request ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Space align="center">
            <UserAvatar src={user?.profile?.avatar} size={44} />
            <div>
              <Typography.Text strong>{getPersonName(user)}</Typography.Text>
              <div>
                <Typography.Text type="secondary">
                  {user?.username || '—'}{' '}
                  {user?.department
                    ? `· ${DEPARTMENT_LABEL[user.department] || user.department}`
                    : ''}
                </Typography.Text>
              </div>
            </div>
          </Space>

          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ID">#{request.id}</Descriptions.Item>
            <Descriptions.Item label="Loại nghỉ">
              {request.leaveType?.name || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày nghỉ">{formatLeaveDays(request.days)}</Descriptions.Item>
            <Descriptions.Item label="Trạng thái">
              <Tag color={LEAVE_STATUS_COLOR[request.status]}>
                {LEAVE_STATUS_LABEL[request.status] || request.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Người duyệt">
              {getPersonName(request.assignedApprover || undefined)}
            </Descriptions.Item>
            <Descriptions.Item label="Đã xử lý bởi">
              {getPersonName(request.approvedBy || undefined)}
            </Descriptions.Item>
            <Descriptions.Item label="Thời gian xử lý">
              {dateTime(request.approvedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Tạo lúc">{dateTime(request.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Lý do">{request.reason || '—'}</Descriptions.Item>
            <Descriptions.Item label="Ghi chú duyệt">{request.reviewNote || '—'}</Descriptions.Item>
          </Descriptions>

          <List
            size="small"
            header={<Typography.Text strong>Danh sách ngày nghỉ</Typography.Text>}
            bordered
            dataSource={request.days}
            renderItem={(day) => <List.Item>{dayjs(day.date).format('DD/MM/YYYY')}</List.Item>}
          />

          {canReviewRequest && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button icon={<CloseOutlined />} danger onClick={() => onReview(request, 'reject')}>
                Từ chối
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => onReview(request, 'approve')}
              >
                Duyệt
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Typography.Text type="secondary">Không có dữ liệu.</Typography.Text>
      )}
    </Drawer>
  );
};

export default LeaveRequestDetailDrawer;
